import { useState } from 'react';
import ServiceStep, { ServiceOption, SERVICES } from './ServiceStep';
import VehicleStep, { VehicleType, VEHICLE_TYPES, AGE_OPTIONS } from './VehicleStep';
import ResultPanel from './ResultPanel';

type Step = 'service' | 'vehicle' | 'result';

const STEP_CONFIG = [
  { id: 'service', label: 'Servicio', icon: 'ri-tools-line', num: 1 },
  { id: 'vehicle', label: 'Vehículo', icon: 'ri-car-line', num: 2 },
  { id: 'result', label: 'Resultado', icon: 'ri-price-tag-3-line', num: 3 },
];

const CalculadoraWizard = () => {
  const [step, setStep] = useState<Step>('service');
  const [selectedVehicleType, setSelectedVehicleType] = useState<VehicleType | null>(VEHICLE_TYPES[1]);
  const [selectedAge, setSelectedAge] = useState<typeof AGE_OPTIONS[number] | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceOption | null>(null);

  const currentIdx = STEP_CONFIG.findIndex((s) => s.id === step);

  const canGoNext =
    (step === 'service' && selectedService !== null) ||
    (step === 'vehicle' && selectedVehicleType !== null && selectedAge !== null);

  const handleNext = () => {
    if (step === 'service') setStep('vehicle');
    else if (step === 'vehicle') setStep('result');
  };

  const handleBack = () => {
    if (step === 'vehicle') setStep('service');
    else if (step === 'result') setStep('vehicle');
  };

  const handleReset = () => {
    setStep('service');
    setSelectedService(null);
    setSelectedVehicleType(VEHICLE_TYPES[1]);
    setSelectedAge(null);
  };

  const handleSelectService = (s: ServiceOption) => {
    setSelectedService(s);
  };

  return (
    <section className="bg-zinc-950 py-16 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-0 mb-12">
          {STEP_CONFIG.map((s, i) => {
            const isDone = currentIdx > i;
            const isActive = currentIdx === i;
            return (
              <div key={s.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 flex items-center justify-center border-2 transition-all duration-300 ${
                      isDone
                        ? 'bg-yellow-400 border-yellow-400 text-black'
                        : isActive
                        ? 'border-yellow-400 text-yellow-400 bg-transparent'
                        : 'border-zinc-700 text-zinc-600 bg-transparent'
                    }`}
                  >
                    {isDone ? (
                      <i className="ri-check-line text-sm font-bold" />
                    ) : (
                      <i className={`${s.icon} text-sm`} />
                    )}
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider mt-1.5 whitespace-nowrap ${
                      isActive ? 'text-yellow-400' : isDone ? 'text-zinc-400' : 'text-zinc-700'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEP_CONFIG.length - 1 && (
                  <div
                    className={`w-16 md:w-24 h-px mx-2 mb-4 transition-all duration-500 ${
                      currentIdx > i ? 'bg-yellow-400' : 'bg-zinc-800'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step title */}
        <div className="text-center mb-8">
          {step === 'service' && (
            <div>
              <h2 className="text-white text-xl md:text-2xl font-bold mb-1">
                <span className="text-yellow-400">Paso 1</span> — ¿Qué servicio necesitas?
              </h2>
            </div>
          )}
          {step === 'vehicle' && (
            <h2 className="text-white text-xl md:text-2xl font-bold mb-1">
              <span className="text-yellow-400">Paso 2</span> — Cuéntanos sobre tu vehículo
            </h2>
          )}
          {step === 'result' && (
            <h2 className="text-white text-xl md:text-2xl font-bold mb-1">
              <span className="text-yellow-400">Paso 3</span> — Tu estimación de precio
            </h2>
          )}
        </div>

        {/* Content */}
        <div className="min-h-64">
          {step === 'service' && (
            <ServiceStep
              selected={selectedService}
              onSelect={handleSelectService}
              vehicleType={selectedVehicleType?.id ?? 'utilitario'}
            />
          )}
          {step === 'vehicle' && (
            <VehicleStep
              selectedType={selectedVehicleType}
              selectedAge={selectedAge}
              onSelectType={(v) => {
                setSelectedVehicleType(v);
                const notApplicable = selectedService?.notApplicable ?? [];
                if (notApplicable.includes(v.id)) {
                  const firstAvailable = SERVICES.find((s) => !s.notApplicable?.includes(v.id));
                  if (firstAvailable) setSelectedService(firstAvailable);
                }
              }}
              onSelectAge={setSelectedAge}
            />
          )}
          {step === 'result' && selectedService && selectedVehicleType && selectedAge && (
            <ResultPanel
              service={selectedService}
              vehicle={selectedVehicleType}
              ageMultiplier={selectedAge.multiplier}
              ageLabel={selectedAge.label}
              onReset={handleReset}
            />
          )}
        </div>

        {/* Navigation buttons */}
        {step !== 'result' && (
          <div className="flex items-center justify-between mt-10 pt-8 border-t border-zinc-800">
            <button
              onClick={handleBack}
              disabled={step === 'service'}
              className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-5 py-3 border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                step === 'service'
                  ? 'border-zinc-800 text-zinc-700 cursor-not-allowed'
                  : 'border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white'
              }`}
            >
              <i className="ri-arrow-left-line" />
              Anterior
            </button>

            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-6 py-3 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                canGoNext
                  ? 'bg-yellow-400 text-black hover:bg-yellow-300'
                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
              }`}
            >
              {step === 'vehicle' ? 'Ver estimación' : 'Siguiente'}
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default CalculadoraWizard;
